import { useState } from 'react';
import {
  App,
  Avatar,
  Button,
  Drawer,
  Empty,
  Form,
  List,
  Popconfirm,
  Select,
  Space,
  Tag,
  Typography,
} from 'antd';
import { AppstoreOutlined, DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useApps } from '@/hooks/useApps';
import {
  useAddAppToProfile,
  useProfile,
  useRemoveProfileApp,
  useUpdateProfileApp,
} from '@/hooks/useProfiles';
import type { ProfileAppDto, ProfileDto } from '@/types/profile';
import {
  AppInstallTypeLabels,
  AppInstallTypeMap,
  AutoUpdateModeLabels,
  AutoUpdateModeMap,
  DefaultPermissionPolicyLabels,
  DefaultPermissionPolicyMap,
} from '@/types/profile';

const { Text, Title } = Typography;

interface Props {
  profile: ProfileDto | null;
  onClose: () => void;
}

interface AddFormValues {
  appId: number;
  installType: number;
  autoUpdateMode: number;
  defaultPermissionPolicy: number;
}

export function ProfileAppsModal({ profile, onClose }: Props) {
  const { message } = App.useApp();
  const [form] = Form.useForm<AddFormValues>();

  const [editingAppId, setEditingAppId] = useState<number | null>(null);
  const [editValues, setEditValues] = useState<AddFormValues | null>(null);

  // Reload del profile con apps frescas
  const { data: detail } = useProfile(profile?.id ?? null);
  const currentProfile = detail ?? profile;

  const { data: appsCatalog } = useApps({ onlyApproved: true, pageSize: 100 });
  const addMut = useAddAppToProfile();
  const updateMut = useUpdateProfileApp();
  const removeMut = useRemoveProfileApp();

  // Apps disponibles para agregar (las que estan aprobadas y no estan ya en el perfil)
  const assignedAppIds = new Set((currentProfile?.apps ?? []).map((pa) => pa.appId));
  const availableApps = (appsCatalog?.items ?? []).filter((a) => !assignedAppIds.has(a.id));

  const handleAdd = async () => {
    if (!currentProfile) return;
    try {
      const values = await form.validateFields();
      await addMut.mutateAsync({
        profileId: currentProfile.id,
        request: values,
      });
      message.success('App agregada al perfil.');
      form.resetFields();
    } catch (err: any) {
      if (err?.errorFields) return;
    }
  };

  const handleStartEdit = (pa: ProfileAppDto) => {
    setEditingAppId(pa.id);
    setEditValues({
      appId: pa.appId,
      installType: pa.installType,
      autoUpdateMode: pa.autoUpdateMode,
      defaultPermissionPolicy: pa.defaultPermissionPolicy,
    });
  };

  const handleSaveEdit = async () => {
    if (!currentProfile || !editingAppId || !editValues) return;
    try {
      await updateMut.mutateAsync({
        profileId: currentProfile.id,
        profileAppId: editingAppId,
        request: {
          installType: editValues.installType,
          autoUpdateMode: editValues.autoUpdateMode,
          defaultPermissionPolicy: editValues.defaultPermissionPolicy,
        },
      });
      message.success('Configuracion actualizada.');
      setEditingAppId(null);
      setEditValues(null);
    } catch {
      // interceptor
    }
  };

  const handleRemove = async (profileAppId: number) => {
    if (!currentProfile) return;
    try {
      await removeMut.mutateAsync({ profileId: currentProfile.id, profileAppId });
      message.success('App removida del perfil.');
    } catch {
      // interceptor
    }
  };

  const installTypeOptions = Object.entries(AppInstallTypeMap).map(([k, v]) => ({
    value: Number(k),
    label: AppInstallTypeLabels[v],
  }));
  const autoUpdateOptions = Object.entries(AutoUpdateModeMap).map(([k, v]) => ({
    value: Number(k),
    label: AutoUpdateModeLabels[v],
  }));
  const permissionOptions = Object.entries(DefaultPermissionPolicyMap).map(([k, v]) => ({
    value: Number(k),
    label: DefaultPermissionPolicyLabels[v],
  }));

  return (
    <Drawer
      title={currentProfile ? `Apps del perfil: ${currentProfile.displayNameEs}` : 'Apps del perfil'}
      open={profile != null}
      onClose={onClose}
      width={680}
      destroyOnClose
    >
      {/* Form para agregar nueva app */}
      <div style={{ marginBottom: 24, padding: 16, background: 'var(--color-bg-elevated)', borderRadius: 8 }}>
        <Title level={5} style={{ marginTop: 0 }}>Agregar app al perfil</Title>

        <Form
          form={form}
          layout="vertical"
          initialValues={{ installType: 0, autoUpdateMode: 2, defaultPermissionPolicy: 0 }}
        >
          <Form.Item
            label="App"
            name="appId"
            rules={[{ required: true, message: 'Requerido' }]}
          >
            <Select
              placeholder={availableApps.length === 0 ? 'No hay apps aprobadas disponibles' : 'Seleccionar app'}
              options={availableApps.map((a) => ({
                value: a.id,
                label: `${a.title} (${a.packageName})`,
              }))}
              showSearch
              optionFilterProp="label"
              disabled={availableApps.length === 0}
            />
          </Form.Item>

          <Space style={{ width: '100%', marginBottom: 16 }} wrap>
            <Form.Item label="Instalacion" name="installType" style={{ marginBottom: 0, minWidth: 180 }}>
              <Select options={installTypeOptions} />
            </Form.Item>
            <Form.Item label="Auto-update" name="autoUpdateMode" style={{ marginBottom: 0, minWidth: 180 }}>
              <Select options={autoUpdateOptions} />
            </Form.Item>
            <Form.Item label="Permisos" name="defaultPermissionPolicy" style={{ marginBottom: 0, minWidth: 160 }}>
              <Select options={permissionOptions} />
            </Form.Item>
          </Space>

          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
            loading={addMut.isPending}
            disabled={availableApps.length === 0}
          >
            Agregar
          </Button>
        </Form>
      </div>

      {/* Listado de apps asignadas */}
      <Title level={5}>Apps asignadas ({currentProfile?.apps.length ?? 0})</Title>

      {(currentProfile?.apps.length ?? 0) === 0 ? (
        <Empty description="Sin apps asignadas" />
      ) : (
        <List
          dataSource={currentProfile?.apps ?? []}
          renderItem={(pa) => {
            const isEditing = editingAppId === pa.id;
            return (
              <List.Item
                actions={
                  isEditing
                    ? [
                        <Button key="save" type="primary" size="small" loading={updateMut.isPending} onClick={handleSaveEdit}>
                          Guardar
                        </Button>,
                        <Button key="cancel" size="small" onClick={() => { setEditingAppId(null); setEditValues(null); }}>
                          Cancelar
                        </Button>,
                      ]
                    : [
                        <Button key="edit" type="text" icon={<EditOutlined />} onClick={() => handleStartEdit(pa)} />,
                        <Popconfirm
                          key="del"
                          title="Quitar esta app del perfil?"
                          onConfirm={() => handleRemove(pa.id)}
                          okText="Si"
                          cancelText="No"
                        >
                          <Button danger type="text" icon={<DeleteOutlined />} loading={removeMut.isPending} />
                        </Popconfirm>,
                      ]
                }
              >
                <List.Item.Meta
                  avatar={
                    <Avatar
                      shape="square"
                      size={48}
                      src={pa.appIconUrl || undefined}
                      icon={<AppstoreOutlined />}
                      style={{ backgroundColor: 'var(--color-bg-container)' }}
                    />
                  }
                  title={pa.appTitle}
                  description={
                    <Space direction="vertical" size={4} style={{ width: '100%' }}>
                      <Text code style={{ fontSize: 12 }}>{pa.appPackageName}</Text>
                      {isEditing && editValues ? (
                        <Space wrap>
                          <Select
                            value={editValues.installType}
                            options={installTypeOptions}
                            onChange={(v) => setEditValues({ ...editValues, installType: v })}
                            style={{ width: 180 }}
                          />
                          <Select
                            value={editValues.autoUpdateMode}
                            options={autoUpdateOptions}
                            onChange={(v) => setEditValues({ ...editValues, autoUpdateMode: v })}
                            style={{ width: 180 }}
                          />
                          <Select
                            value={editValues.defaultPermissionPolicy}
                            options={permissionOptions}
                            onChange={(v) => setEditValues({ ...editValues, defaultPermissionPolicy: v })}
                            style={{ width: 160 }}
                          />
                        </Space>
                      ) : (
                        <Space wrap size={[4, 4]}>
                          <Tag color="blue">{AppInstallTypeLabels[AppInstallTypeMap[pa.installType]]}</Tag>
                          <Tag color="cyan">{AutoUpdateModeLabels[AutoUpdateModeMap[pa.autoUpdateMode]]}</Tag>
                          <Tag color="purple">{DefaultPermissionPolicyLabels[DefaultPermissionPolicyMap[pa.defaultPermissionPolicy]]}</Tag>
                        </Space>
                      )}
                    </Space>
                  }
                />
              </List.Item>
            );
          }}
        />
      )}
    </Drawer>
  );
}
